import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { IShareCustomReportResponse, ICustomReportShareListResponse } from '@shared/interfaces/custom-report-share.interface';
import { ITenantUserSearchResult } from '@shared/interfaces/user.interface';
import { AcademyUserRole } from '@shared/constants/academy-user';
import { Api, ApiHeaderOption, ApiQueryOption } from '@shared/constants/api';
import { AccessibilityAuditToolNames } from '@shared/constants/audit-tool';
import { FileExtension } from '@shared/constants/file-extension';
import { $tenant } from '@shared/constants/tenant';
import { ITenantFeatureFlagsRequest, ITenantFeatureFlagsResponse } from '@shared/interfaces/tenant-feature-flag.interface';
import { ICustomReportExportResponse, IOrganizationReportResponse } from '@shared/interfaces/async-report.interface';
import {
  ICustomReportListQuery,
  ICustomReportListResponse,
  ICustomReportSavedView,
  ICustomReportStatusRequest,
  IDuplicateCustomReportRequest,
  IReportingAgentSessionResponse,
  IRestoreReportingAgentSessionRequest,
  ISubmitReportingAgentTurnRequest,
  IUpdateCustomReportFavouriteRequest,
  IUpdateCustomReportRequest,
} from '@shared/interfaces/custom-reports.interface';
import {
  ICreateCustomReportScheduleRequest,
  ICustomReportSchedule,
  IUpdateCustomReportScheduleRequest,
} from '@shared/interfaces/custom-report-schedule.interface';
import { IReportingAgentReport, IReportingAgentTurnResponse } from '@shared/interfaces/reporting-agent.interface';
import {
  IGetDigitalPropertyMetricsSnapshotsQuery,
  IPaginatedDigitalPropertyMetricsList,
} from '@shared/interfaces/digital-property-metrics.interface';
import { IDigitalPropertiesListResponse } from '@shared/interfaces/digital-property.interface';
import { IScannableDocumentPdfPagesTestStateResponse } from '@shared/interfaces/scannable-document.interface';
import { ITenantMetrics } from '@shared/interfaces/tenant-metrics.interface';
import {
  IAssignAcademyRolesRequest,
  IBatchUserIdRequest,
  ICreateTenantRequest,
  IEditTenantRequest,
  ITenant,
  ITenantDigitalPropertyLimitState,
  ITenantDigitalPropertyUserWaySiteState,
  ITenantInfoWithPackageInfo,
  ITenantScimConfig,
  ITenantsResponse,
  ITenantSsoConfig,
  ITenantUser,
  ITenantWithPackageId,
} from '@shared/interfaces/tenant.interface';
import {
  IUserWayAccountCode,
  IUserWaySSOLink,
  IUserWayTenantIntegration,
  UserWayOrganizationAuthRequestTarget,
} from '@shared/interfaces/userway-integration.interface';
import { IWorkspaceWithDpCount } from '@shared/interfaces/workspace.interface';
import { DashboardDataFrequency } from '@shared/interfaces/ws-dashboard.interface';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { RestBuilder } from '../helpers/rest.builder';

@Injectable({ providedIn: 'root' })
export class TenantApi {
  private restBuilder: RestBuilder;

  constructor(private httpClient: HttpClient) {
    this.restBuilder = new RestBuilder();
  }

  // GET /tenants/current
  public getCurrentTenant(): Observable<ITenantInfoWithPackageInfo> {
    const url: string = this.restBuilder.create().tenant().current().getApiUrl();
    return this.httpClient.get<ITenantInfoWithPackageInfo>(url);
  }

  // POST /tenants/current/tenant-feature-flags
  public getTenantFeatureFlags(keys: string[]): Observable<ITenantFeatureFlagsResponse> {
    const url: string = this.restBuilder.create().tenant().current().tenantFeatureFlags().getApiUrl();
    return this.httpClient.post<ITenantFeatureFlagsResponse>(url, { keys } as ITenantFeatureFlagsRequest);
  }

  // GET /admin/tenants/:tenantId
  public getTenant(tenantId: string): Observable<ITenantWithPackageId> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).getApiUrl();
    return this.httpClient.get<ITenantWithPackageId>(url);
  }

  // GET /admin/tenants/:tenantId/digital-properties
  public getAllDigitalAssets(tenantId: string): Observable<IDigitalPropertiesListResponse> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).digitalProperties().getApiUrl();
    return this.httpClient.get<IDigitalPropertiesListResponse>(url);
  }

  // GET /tenants/:tenantId
  public getTenantFromTenantedScope(tenantId: string): Observable<ITenantInfoWithPackageInfo> {
    const url: string = this.restBuilder.create().tenant(tenantId).getApiUrl();
    return this.httpClient.get<ITenantInfoWithPackageInfo>(url);
  }

  // GET /tenants
  public getUserTenantsFromTenantedScope(): Observable<ITenant[]> {
    const url: string = this.restBuilder.create().tenant().getApiUrl();
    return this.httpClient.get<ITenant[]>(url);
  }

  // PUT /admin/tenants/:tenantId
  public updateTenant(tenantId: string, updateRequest: IEditTenantRequest): Observable<ITenant> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).getApiUrl();
    return this.httpClient.put<ITenant>(url, updateRequest);
  }

  // GET /admin/tenants/:tenantId/workspaces
  public getWorkspaces(tenantId: string, searchString?: string): Observable<IWorkspaceWithDpCount[]> {
    const urlBuilder: RestBuilder = this.restBuilder.create().admin().tenant(tenantId).workspaces();
    if (!SharedCommonUtility.isNullish(searchString)) {
      urlBuilder.query(
        new HttpParams({
          fromObject: {
            [Api.clientSearchTerm]: searchString,
          },
        }),
      );
    }
    return this.httpClient.get<IWorkspaceWithDpCount[]>(urlBuilder.getApiUrl());
  }

  // GET /admin/tenants
  public getTenants(
    page: number = 0,
    limit: number = 0,
    term: string = '',
    sortBy?: string,
    direction?: string,
  ): Observable<ITenantsResponse> {
    const url: string = this.restBuilder.create().admin().tenant().getApiUrl();
    const httpParams: HttpParams = this.buildGetTenantsHttpParams(page, limit, term, sortBy, direction);

    return this.httpClient.get<ITenantsResponse>(url, { params: httpParams });
  }

  // GET /admin/tenants/my
  public getMyTenants(
    page: number = 0,
    limit: number = 0,
    term: string = '',
    sortBy?: string,
    direction?: string,
  ): Observable<ITenantsResponse> {
    const url: string = this.restBuilder.create().admin().tenant().my().getApiUrl();
    const httpParams: HttpParams = this.buildGetTenantsHttpParams(page, limit, term, sortBy, direction);

    return this.httpClient.get<ITenantsResponse>(url, { params: httpParams });
  }

  private buildGetTenantsHttpParams(
    page: number = 0,
    limit: number = 0,
    term: string = '',
    sortBy?: string,
    direction?: string,
  ): HttpParams {
    const params: { [param: string]: any } = {};

    if (typeof limit === 'number') {
      params[ApiQueryOption.limit] = String(limit);
    }

    if (typeof page === 'number') {
      params[ApiQueryOption.page] = String(page);
    }

    if (typeof sortBy === 'string') {
      params[ApiQueryOption.sortBy] = sortBy;
    }

    if (typeof direction === 'string') {
      params[ApiQueryOption.direction] = direction;
    }

    if (term?.length > 0) {
      params[ApiQueryOption.term] = window.encodeURIComponent(term.trim());
    }

    return new HttpParams({ fromObject: params });
  }

  // POST /admin/tenants
  public createTenant(createRequest: ICreateTenantRequest): Observable<ITenant> {
    const url: string = this.restBuilder.create().admin().tenant().getApiUrl();

    return this.httpClient.post<ITenant>(url, createRequest);
  }

  // PUT /admin/tenants/:tenantId/sso-config
  public setSsoConfig(tenantId: string, ssoConfig: ITenantSsoConfig): Observable<void> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).ssoConfig().getApiUrl();
    return this.httpClient.put<void>(url, ssoConfig);
  }

  // DELETE /admin/tenants/:tenantId/sso-config?reason=Reason to delete
  public removeSsoConfig(tenantId: string, reason: string): Observable<void> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).ssoConfig().getApiUrl();
    return this.httpClient.delete<void>(url, { params: { reason } });
  }

  // DELETE /admin/tenants/:tenantId (decommission; 202 Accepted, empty body)
  public decommissionTenant(tenantId: string): Observable<void> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).getApiUrl();
    return this.httpClient.delete<void>(url);
  }

  public suspendTenant(tenantId: string): Observable<ITenant> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).suspend().getApiUrl();
    return this.httpClient.post<ITenant>(url, {});
  }

  public resumeTenant(tenantId: string): Observable<ITenant> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).resume().getApiUrl();
    return this.httpClient.post<ITenant>(url, {});
  }

  // GET /admin/tenants/:tenantId/pdf-pages-test-state
  public getPdfPagesTestState(tenantId: string): Observable<IScannableDocumentPdfPagesTestStateResponse> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).pdfPagesTestState().getApiUrl();
    return this.httpClient.get<IScannableDocumentPdfPagesTestStateResponse>(url);
  }

  // GET /admin/tenants/:tenantId/users
  public getTenantUsers(tenantId: string): Observable<ITenantUser[]> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).users().getApiUrl();
    return this.httpClient.get<ITenantUser[]>(url);
  }

  // PUT /admin/tenants/:tenantId/users/academy/activate
  public activateUserInAcademy(tenantId: string, userId: string): Observable<void> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).users().academy().activate().getApiUrl();
    return this.httpClient.put<void>(url, { userId } as IBatchUserIdRequest);
  }

  // PUT /admin/tenants/:tenantId/users/academy/deactivate
  public deactivateUserInAcademy(tenantId: string, userId: string): Observable<void> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).users().academy().deactivate().getApiUrl();
    return this.httpClient.put<void>(url, { userId } as IBatchUserIdRequest);
  }

  // PUT /admin/tenants/:tenantId/users/academy/roles
  public setUserRolesInAcademy(tenantId: string, userId: string, roles: AcademyUserRole[]): Observable<void> {
    const url: string = this.restBuilder.create().admin().tenant(tenantId).users().academy().roles().getApiUrl();
    return this.httpClient.put<void>(url, { userId, roles } as IAssignAcademyRolesRequest);
  }

  // GET /tenants/:tenantId/digital-property-limit-state
  public getDigitalPropertyLimitState(tenantId: string): Observable<ITenantDigitalPropertyLimitState | null> {
    const url: string = this.restBuilder.create().tenant(tenantId).digitalPropertyLimitState().getApiUrl();
    return this.httpClient.get<ITenantDigitalPropertyLimitState>(url);
  }

  // GET /tenants/current/userway_site_limit_state
  // GET /tenants/:tenantId/userway_site_limit_state
  public getDigitalPropertyUserWaySiteLimitState(tenantId?: string): Observable<ITenantDigitalPropertyUserWaySiteState | null> {
    const url: string = tenantId
      ? this.restBuilder.create().tenant(tenantId).digitalPropertyUserWaySiteLimitState().getApiUrl()
      : this.restBuilder.create().tenant().current().digitalPropertyUserWaySiteLimitState().getApiUrl();
    return this.httpClient.get<ITenantDigitalPropertyUserWaySiteState>(url);
  }

  // GET /tenants/:tenantId/dashboard/metrics
  public getTenantMetrics(
    tenantId: string,
    toolName: AccessibilityAuditToolNames,
    frequency: DashboardDataFrequency,
  ): Observable<ITenantMetrics[]> {
    const url: string = this.restBuilder.create().tenant(tenantId).dashboard().metrics().getApiUrl();
    const httpParams: HttpParams = new HttpParams({
      fromObject: { [ApiQueryOption.toolName]: toolName, [ApiQueryOption.frequency]: frequency },
    });
    return this.httpClient.get<ITenantMetrics[]>(url, { params: httpParams });
  }

  // GET /tenants/:tenantId/dashboard/digital-property-metrics
  public getDigitalPropertyMetricsSnapshots(
    tenantId: string,
    params: IGetDigitalPropertyMetricsSnapshotsQuery,
  ): Observable<IPaginatedDigitalPropertyMetricsList> {
    const url: string = this.restBuilder.create().tenant(tenantId).dashboard().digitalPropertyMetrics().getApiUrl();
    const httpParams: HttpParams = new HttpParams({
      fromObject: { ...params },
    });
    return this.httpClient.get<IPaginatedDigitalPropertyMetricsList>(url, { params: httpParams });
  }

  // PUT /tenants/current/cloudsmith-token
  public getOrCreateTenantEntitlementToken(): Observable<Pick<ITenant, $tenant.cloudsmithToken>> {
    const url: string = this.restBuilder.create().tenant().current().cloudsmithToken().getApiUrl();
    return this.httpClient.put<Pick<ITenant, $tenant.cloudsmithToken>>(url, {});
  }

  // POST /tenants/current/scanning-agent/generate-encryption-key
  public generateScanningAgentEncryptionKey(): Observable<string> {
    const url: string = this.restBuilder.create().tenant().current().scanningAgent().generateEncryptionKey().getApiUrl();
    return this.httpClient.post(url, {}, { responseType: 'text' });
  }

  // GET /tenants/current/cloudsmith-token
  public getTenantEntitlementToken(): Observable<Pick<ITenant, $tenant.cloudsmithToken>> {
    const url: string = this.restBuilder.create().tenant().current().cloudsmithToken().getApiUrl();
    return this.httpClient.get<Pick<ITenant, $tenant.cloudsmithToken>>(url);
  }

  // POST /tenants/current/cloudsmith-token/rotate
  public rotateTenantEntitlementToken(): Observable<Pick<ITenant, $tenant.cloudsmithToken>> {
    const url: string = this.restBuilder.create().tenant().current().cloudsmithToken().rotate().getApiUrl();
    return this.httpClient.post<Pick<ITenant, $tenant.cloudsmithToken>>(url, {});
  }

  // PUT /tenants/current/docker-token
  public getOrCreateTenantDockerToken(): Observable<Pick<ITenant, $tenant.dockerCloudsmithToken>> {
    const url: string = this.restBuilder.create().tenant().current().dockerToken().getApiUrl();
    return this.httpClient.put<Pick<ITenant, $tenant.dockerCloudsmithToken>>(url, {});
  }

  // GET /tenants/:tenantId/dashboard/report
  public downloadOrganizationReport(
    tenantId: string,
    toolName: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
  ): Observable<IOrganizationReportResponse> {
    const url: string = this.restBuilder.create().tenant(tenantId).dashboard().report().getApiUrl();
    const httpParams: HttpParams = new HttpParams({
      fromObject: {
        [ApiQueryOption.toolName]: toolName,
        [ApiQueryOption.format]: fileExtension,
      },
    });
    return this.httpClient.get<IOrganizationReportResponse>(url, { params: httpParams });
  }

  // POST /tenants/:tenantId/dashboard/report
  public generateOrganizationReport(
    tenantId: string,
    toolName: AccessibilityAuditToolNames,
    fileExtension: FileExtension,
  ): Observable<IOrganizationReportResponse> {
    const url: string = this.restBuilder.create().tenant(tenantId).dashboard().report().getApiUrl();
    return this.httpClient.post<IOrganizationReportResponse>(url, {
      [ApiQueryOption.toolName]: toolName,
      [ApiQueryOption.format]: fileExtension,
    });
  }

  // GET /tenants/current/scim-config
  public getScimConfig(): Observable<ITenantScimConfig> {
    const url: string = this.restBuilder.create().tenant().current().scimConfig().getApiUrl();

    return this.httpClient.get<ITenantScimConfig>(url);
  }

  // POST /tenants/current/scim-config
  public setScimConfig(config: ITenantScimConfig): Observable<ITenantScimConfig> {
    const url: string = this.restBuilder.create().tenant().current().scimConfig().getApiUrl();

    return this.httpClient.put<ITenantScimConfig>(url, config);
  }

  // GET /tenants/current/userway-sso-link
  public generateUserWaySSOLink(
    target: UserWayOrganizationAuthRequestTarget,
    userwayAccountCode?: string,
  ): Observable<IUserWaySSOLink> {
    const url: string = this.restBuilder.create().tenant().current().userWaySSOLink().getApiUrl();

    const httpParams: HttpParams = new HttpParams({
      fromObject: {
        [ApiQueryOption.target]: target,
        ...(userwayAccountCode ? { [ApiQueryOption.userwayAccountCode]: userwayAccountCode } : {}),
      },
    });
    return this.httpClient.get<IUserWaySSOLink>(url, { params: httpParams });
  }

  public generateUserWaySSORedirectUrl(
    target: UserWayOrganizationAuthRequestTarget,
    userwayAccountCode?: string,
    additionalQueryParams?: URLSearchParams,
  ): string {
    const params: Record<string, string> = {
      [ApiQueryOption.target]: target,
      [ApiQueryOption.redirectToSso]: String(true),
      ...(userwayAccountCode ? { [ApiQueryOption.userwayAccountCode]: userwayAccountCode } : {}),
    };

    additionalQueryParams?.forEach((value: string, key: string): void => {
      if (SharedCommonUtility.notNullishOrEmpty(value)) {
        params[key] = value;
      }
    });

    return this.restBuilder
      .create()
      .tenant()
      .current()
      .userWaySSOLink()
      .query(new HttpParams({ fromObject: params }))
      .getApiUrl();
  }

  // GET /tenants/current/userway-account-code
  public getUserWayAccountCode(): Observable<IUserWayAccountCode> {
    const url: string = this.restBuilder.create().tenant().current().getUserWayAccountCode().getApiUrl();

    return this.httpClient.get<IUserWayAccountCode>(url);
  }

  // GET /tenants/current/userway
  public getUserWayAccess(): Observable<IUserWayTenantIntegration> {
    const url: string = this.restBuilder.create().tenant().current().userway().getApiUrl();

    return this.httpClient.get<IUserWayTenantIntegration>(url);
  }

  // GET /tenants/current/custom-reports/export
  public getCustomReportExportStatus(reportId: string): Observable<ICustomReportExportResponse> {
    const url: string = this.restBuilder.create().tenant().current().customReports().export().getApiUrl();
    const httpParams: HttpParams = new HttpParams({
      fromObject: {
        [ApiQueryOption.customReportExportKey]: reportId,
      },
    });

    return this.httpClient.get<ICustomReportExportResponse>(url, { params: httpParams });
  }

  // POST /tenants/current/custom-reports/:id/export
  public exportCustomReportById(id: string): Observable<ICustomReportExportResponse> {
    const url: string = this.restBuilder.create().tenant().current().customReports(id).export().getApiUrl();
    return this.httpClient.post<ICustomReportExportResponse>(url, {});
  }

  // GET /tenants/current/custom-reports?page=&limit=&sortBy=&direction=&searchTerm=
  public listCustomReports(query: ICustomReportListQuery): Observable<ICustomReportListResponse> {
    const url: string = this.restBuilder.create().tenant().current().customReports().getApiUrl();
    const params: Record<string, string> = {};

    if (SharedCommonUtility.notNullish(query.page)) {
      params[ApiQueryOption.page] = query.page;
    }

    if (SharedCommonUtility.notNullish(query.limit)) {
      params[ApiQueryOption.limit] = query.limit;
    }

    if (SharedCommonUtility.notNullish(query.sortBy)) {
      params[ApiQueryOption.sortBy] = query.sortBy;
    }

    if (SharedCommonUtility.notNullish(query.direction)) {
      params[ApiQueryOption.direction] = query.direction;
    }

    if (SharedCommonUtility.notNullishOrEmpty(query.searchTerm?.trim())) {
      params[ApiQueryOption.searchTerm] = query.searchTerm;
    }

    return this.httpClient.get<ICustomReportListResponse>(url, { params: new HttpParams({ fromObject: params }) });
  }

  // DELETE /tenants/current/custom-reports/:id
  public deleteCustomReport(id: string): Observable<void> {
    const url: string = this.restBuilder.create().tenant().current().customReports(id).getApiUrl();
    return this.httpClient.delete<void>(url);
  }

  // PATCH /tenants/current/custom-reports/:id/favourite
  public updateCustomReportFavourite(id: string, body: IUpdateCustomReportFavouriteRequest): Observable<void> {
    const url: string = this.restBuilder.create().tenant().current().customReports(id).favourite().getApiUrl();
    return this.httpClient.patch<void>(url, body);
  }

  // PATCH /tenants/current/custom-reports/:id
  public updateCustomReport(id: string, body: IUpdateCustomReportRequest): Observable<void> {
    const url: string = this.restBuilder.create().tenant().current().customReports(id).getApiUrl();
    return this.httpClient.patch<void>(url, body);
  }

  // POST /tenants/current/custom-reports/:id/duplicate
  public duplicateCustomReport(id: string, body: IDuplicateCustomReportRequest): Observable<void> {
    const url: string = this.restBuilder.create().tenant().current().customReports(id).duplicate().getApiUrl();
    return this.httpClient.post<void>(url, body);
  }

  // GET /tenants/current/custom-reports/shared/:reportId
  public getSharedCustomReport(reportId: string): Observable<ICustomReportSavedView> {
    const url: string = this.restBuilder.create().tenant().current().customReports().shared(reportId).getApiUrl();
    return this.httpClient.get<ICustomReportSavedView>(url);
  }

  // POST /tenants/current/custom-reports/:id/share
  public shareCustomReportWithUser(id: string, recipientId: string): Observable<IShareCustomReportResponse> {
    const url: string = this.restBuilder.create().tenant().current().customReports(id).share().getApiUrl();
    return this.httpClient.post<IShareCustomReportResponse>(
      url,
      { recipientId },
      { headers: { [ApiHeaderOption.skipLoader]: 'true' } },
    );
  }

  // GET /tenants/current/custom-reports/:id/share
  public getCustomReportShareRecipients(id: string): Observable<ICustomReportShareListResponse> {
    const url: string = this.restBuilder.create().tenant().current().customReports(id).share().getApiUrl();
    return this.httpClient.get<ICustomReportShareListResponse>(url, { headers: { [ApiHeaderOption.skipLoader]: 'true' } });
  }

  // DELETE /tenants/current/custom-reports/:id/share/:recipientId
  public deleteCustomReportShareRecipient(reportId: string, recipientId: string): Observable<void> {
    const url: string = this.restBuilder.create().tenant().current().customReports(reportId).share(recipientId).getApiUrl();
    return this.httpClient.delete<void>(url, { headers: { [ApiHeaderOption.skipLoader]: 'true' } });
  }

  // POST /tenants/current/custom-reports/:id/schedule
  public createCustomReportSchedule(id: string, body: ICreateCustomReportScheduleRequest): Observable<ICustomReportSchedule> {
    const url: string = this.restBuilder.create().tenant().current().customReports(id).schedule().getApiUrl();
    return this.httpClient.post<ICustomReportSchedule>(url, body);
  }

  // GET /tenants/current/custom-reports/:id/schedule
  public getCustomReportSchedule(id: string): Observable<ICustomReportSchedule> {
    const url: string = this.restBuilder.create().tenant().current().customReports(id).schedule().getApiUrl();
    return this.httpClient.get<ICustomReportSchedule>(url);
  }

  // PUT /tenants/current/custom-reports/:id/schedule
  public updateCustomReportSchedule(id: string, body: IUpdateCustomReportScheduleRequest): Observable<ICustomReportSchedule> {
    const url: string = this.restBuilder.create().tenant().current().customReports(id).schedule().getApiUrl();
    return this.httpClient.put<ICustomReportSchedule>(url, body);
  }

  // DELETE /tenants/current/custom-reports/:id/schedule
  public deleteCustomReportSchedule(id: string): Observable<void> {
    const url: string = this.restBuilder.create().tenant().current().customReports(id).schedule().getApiUrl();
    return this.httpClient.delete<void>(url);
  }

  // POST /tenants/current/custom-reports/v3/turns
  public submitReportingAgentTurn(body: ISubmitReportingAgentTurnRequest): Observable<IReportingAgentTurnResponse> {
    const url: string = this.restBuilder.create().tenant().current().customReports().v3().turns().getApiUrl();
    return this.httpClient.post<IReportingAgentTurnResponse>(url, body, {
      headers: { [ApiHeaderOption.skipLoader]: 'true' },
    });
  }

  // GET /tenants/current/custom-reports/v3/status?sessionId=X&requestId=Y
  public getMultiTurnReportStatus(sessionId: string, requestId: string): Observable<IReportingAgentTurnResponse> {
    const url: string = this.restBuilder.create().tenant().current().customReports().v3().status().getApiUrl();
    const httpParams: HttpParams = new HttpParams({ fromObject: { sessionId, requestId } });
    return this.httpClient.get<IReportingAgentTurnResponse>(url, {
      params: httpParams,
      headers: { [ApiHeaderOption.skipLoader]: 'true' },
    });
  }

  // GET /tenants/current/custom-reports/v3/sessions/:sessionId
  public getReportingAgentSession(sessionId: string): Observable<IReportingAgentSessionResponse> {
    const url: string = this.restBuilder.create().tenant().current().customReports().v3().sessions(sessionId).getApiUrl();
    return this.httpClient.get<IReportingAgentSessionResponse>(url, {
      headers: { [ApiHeaderOption.skipLoader]: 'true' },
    });
  }

  // POST /tenants/current/custom-reports/v3/turns/cancel
  public cancelReportingAgentTurn(body: ICustomReportStatusRequest): Observable<void> {
    const url: string = this.restBuilder.create().tenant().current().customReports().v3().turns().cancel().getApiUrl();
    return this.httpClient.post<void>(url, body, {
      headers: { [ApiHeaderOption.skipLoader]: 'true' },
    });
  }

  // POST /tenants/current/custom-reports/v3/sessions/:sessionId/restore
  public restoreReportingAgentVersion(
    sessionId: string,
    body: IRestoreReportingAgentSessionRequest,
  ): Observable<IReportingAgentReport> {
    const url: string = this.restBuilder
      .create()
      .tenant()
      .current()
      .customReports()
      .v3()
      .sessions(sessionId)
      .restore()
      .getApiUrl();
    return this.httpClient.post<IReportingAgentReport>(url, body, {
      headers: { [ApiHeaderOption.skipLoader]: 'true' },
    });
  }

  // GET /tenants/current/users?term=&limit=&excludeSelf=
  public searchTenantUsers(term: string, limit: number, excludeSelf: boolean = false): Observable<ITenantUserSearchResult[]> {
    const url: string = this.restBuilder.create().tenant().current().users().getApiUrl();
    const httpParams: HttpParams = new HttpParams({
      fromObject: {
        [ApiQueryOption.term]: window.encodeURIComponent(term.trim()),
        [ApiQueryOption.limit]: limit,
        [ApiQueryOption.excludeSelf]: excludeSelf,
      },
    });
    return this.httpClient.get<ITenantUserSearchResult[]>(url, {
      params: httpParams,
      headers: {
        [ApiHeaderOption.skipLoader]: 'true',
      },
    });
  }
}
